/**
 * /docs/getting-started — pick a path. Two top-level surfaces:
 *   (1) In-browser chat next to a preview — zero install, the default
 *   (2) Claude Code plugin — for power users / file-level control
 * Everything else (Claude Desktop, Claude.ai web, Cursor) is a footnote
 * <details> at the end of the page; the MCP server URL is the only
 * thing those clients need from us, so a single shared block covers
 * them all.
 */

import { apiOrigin } from '../../api/client.js';
import { IconBolt, IconLaptop, IconSparkle } from '../../components/Icons.js';
import { ButtonLink, PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Callout, CodeBlock, ConnectionStatus, DocSection } from './_shared.js';
import KIN_SKILL_MD from '../../../../../docs/skill/kin-skill.md?raw';

const SKILL_INSTALL_CMD = `mkdir -p ~/.claude/skills/kin && cat > ~/.claude/skills/kin/SKILL.md << 'KIN_SKILL_EOF'
${KIN_SKILL_MD}
KIN_SKILL_EOF`;

const mcpJson = (origin: string) => `{
  "mcpServers": {
    "kin": {
      "type": "http",
      "url": "${origin}/mcp"
    }
  }
}`;

const mcpCli = (origin: string) => `claude mcp add kin --transport http ${origin}/mcp`;

export function DocsGettingStarted() {
  const origin = apiOrigin();

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · Getting started"
        title="Pick a path"
        description="Two surfaces, same backend. Start in the browser; reach for Claude Code when you outgrow it."
      />

      <DocsTabs />

      <DocSection
        id="in-browser"
        title={
          <span className="inline-flex items-center gap-2">
            <IconSparkle size={18} className="text-brand" />
            In the browser
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand bg-brand-soft px-2 py-0.5 rounded">
              Easiest
            </span>
          </span>
        }
      >
        <p>
          Chat right beside your app preview. Zero install &mdash; if you can open this portal, you
          can build. This is where most people will live, especially for the first app and for
          iteration after it&apos;s deployed.
        </p>

        <h3 className="font-medium text-ink mt-5">Create a new app</h3>
        <ol className="list-decimal list-inside space-y-1 pl-1">
          <li>
            Open <strong>Apps</strong>, click <strong>Start building</strong> &rarr;{' '}
            <em>Create app</em>.
          </li>
          <li>Give it a name and a slug. Check the database box if it needs to remember things.</li>
          <li>
            You land on <span className="font-mono text-xs">preview-{`{slug}`}.mkin.app</span> with
            the Kin chat bubble open in the lower-right.
          </li>
          <li>
            Tell the bubble what you want. It writes the code, deploys to preview, and prompts you
            to reload. When the preview looks right, say{' '}
            <em>&ldquo;promote this to live.&rdquo;</em>
          </li>
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          <ButtonLink to="/apps/new" variant="primary" size="sm">
            <IconBolt size={13} />
            Create your first app
          </ButtonLink>
        </div>

        <h3 className="font-medium text-ink mt-6">Edit an existing app</h3>
        <p>
          Open the app from the <strong>Apps</strong> page, then click{' '}
          <strong>Edit in Preview</strong> on the detail page. The preview opens with the chat
          bubble ready &mdash; type changes; the agent applies, redeploys, and prompts to reload.
        </p>

        <Callout tone="tip" title="The bubble appears for managers only.">
          <p>
            End users of your apps don&apos;t see it. You become a manager automatically when you
            create an app; you can add co-managers under the app&apos;s <strong>Access</strong> tab.
          </p>
        </Callout>
      </DocSection>

      <DocSection
        id="claude-code"
        title={
          <span className="inline-flex items-center gap-2">
            <IconLaptop size={18} className="text-muted" />
            Claude Code
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted bg-surface-2 px-2 py-0.5 rounded">
              Advanced
            </span>
          </span>
        }
      >
        <p>
          Anthropic&apos;s CLI. Best for file-level control, multi-app flows, scripting, and anyone
          already comfortable in a terminal. One plugin install wires up both halves &mdash; the
          skill (so Claude knows how to use Kin) and the MCP connector (so it can talk to{' '}
          <span className="font-mono text-xs">{origin}/mcp</span>).
        </p>

        <ConnectionStatus />

        <h3 className="font-medium text-ink mt-5">
          Check first &mdash; it may already be installed
        </h3>
        <p>In any Claude Code session:</p>
        <CodeBlock body="/plugin list" caption="Claude Code" />
        <p>
          Look for <span className="font-mono text-xs">kin@mytra</span>. If it&apos;s there, confirm
          by saying <em>&ldquo;list my Kin apps&rdquo;</em> in a chat &mdash; the banner above flips
          to green on the first authorized call.
        </p>

        <h3 className="font-medium text-ink mt-6">If not, install once</h3>
        <CodeBlock
          body={`/plugin marketplace add MytraAI/kin\n/plugin install kin@mytra`}
          caption="Claude Code"
        />
        <p>Updates ship through the marketplace &mdash; no manual refresh.</p>
        <Callout title="GitHub access required">
          <p>
            The plugin lives in the private <span className="font-mono text-xs">MytraAI/kin</span>{' '}
            repo. Your local <span className="font-mono text-xs">gh</span> CLI must be signed in (
            <span className="font-mono text-xs">gh auth login</span>), or your SSH key must be
            registered with the Mytra GitHub org. Without GitHub access the install fails.
          </p>
        </Callout>

        <details className="rounded-xl border border-line bg-surface mt-5">
          <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-surface-2 rounded-xl">
            Can&apos;t use the plugin? Wire up the two pieces by hand
          </summary>
          <div className="border-t border-line px-5 py-4 space-y-3 text-sm text-ink-2">
            <p>MCP server:</p>
            <CodeBlock body={mcpCli(origin)} caption="add Kin to Claude Code" />
            <p>Or drop this in your project root &mdash; Claude Code picks it up automatically:</p>
            <CodeBlock body={mcpJson(origin)} caption=".mcp.json" />
            <p>Skill:</p>
            <CodeBlock
              body={SKILL_INSTALL_CMD}
              caption="install ~/.claude/skills/kin/SKILL.md"
              maxHeight="14rem"
            />
            <p className="text-muted">
              Re-run the same command later to update; or use the plugin path above and let the
              marketplace handle it. The plugin is the recommended path once GitHub access is
              sorted.
            </p>
          </div>
        </details>
      </DocSection>

      <DocSection title="Other clients (footnote)">
        <p>
          Kin&apos;s MCP server is plain HTTP &mdash; any client that speaks MCP can connect. Claude
          Desktop, Claude.ai web, Cursor, and most other AI clients work the same way: point them at{' '}
          <span className="font-mono text-xs">{origin}/mcp</span> and (where the client supports it)
          load the Kin skill as custom instructions or rules.
        </p>
        <p className="text-muted text-sm">
          We don&apos;t actively maintain step-by-step instructions for these &mdash; the in-browser
          chat covers most of what they offered, and the Claude Code plugin covers the rest. If you
          have a strong preference for one of these clients, the steps below get you wired up.
        </p>

        <details className="rounded-xl border border-line bg-surface mt-3">
          <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-surface-2 rounded-xl">
            Show MCP setup for Claude Desktop, Claude.ai web, and Cursor
          </summary>
          <div className="border-t border-line px-5 py-4 space-y-5 text-sm text-ink-2">
            <div>
              <h4 className="font-medium text-ink">Claude Desktop</h4>
              <p className="mt-1">Edit the config file:</p>
              <CodeBlock
                body="~/Library/Application Support/Claude/claude_desktop_config.json"
                caption="macOS"
                noCopy
              />
              <CodeBlock
                body="%APPDATA%\\Claude\\claude_desktop_config.json"
                caption="Windows"
                noCopy
              />
              <CodeBlock body={mcpJson(origin)} caption="claude_desktop_config.json" />
              <p className="text-muted">
                Restart Claude Desktop. For the skill, create a project named &ldquo;Kin&rdquo;,
                open <strong>Custom Instructions</strong>, and paste the skill markdown (disclosed
                below).
              </p>
            </div>

            <div>
              <h4 className="font-medium text-ink">Claude.ai (web)</h4>
              <p className="mt-1">
                Settings &rarr; <strong>Connectors</strong> &rarr; add a custom connector with URL{' '}
                <span className="font-mono text-xs">{origin}/mcp</span>. OAuth runs once on first
                use.
              </p>
              <p className="text-muted mt-1">
                Mytra-managed Claude.ai accounts usually inherit Kin as an org connector + org
                skill, so no manual setup is needed. If yours hasn&apos;t, paste the skill markdown
                (below) into a project&apos;s Custom Instructions.
              </p>
            </div>

            <div>
              <h4 className="font-medium text-ink">Cursor</h4>
              <p className="mt-1">
                <strong>Settings &rarr; MCP &rarr; Add new MCP server:</strong>
              </p>
              <CodeBlock
                body={`Name: kin\nType: http\nURL: ${origin}/mcp`}
                caption="Cursor MCP config"
              />
              <p className="text-muted mt-1">
                Cursor uses Rules in place of skills &mdash; drop the skill markdown into{' '}
                <span className="font-mono text-xs">.cursor/rules/kin.md</span> or a User Rule.
              </p>
            </div>

            <details className="rounded-xl border border-line bg-surface mt-2">
              <summary className="cursor-pointer px-4 py-2.5 text-xs font-medium text-muted hover:bg-surface-2 rounded-xl">
                Show full skill markdown
              </summary>
              <div className="border-t border-line">
                <CodeBlock body={KIN_SKILL_MD} caption="kin skill" maxHeight="20rem" />
              </div>
            </details>
          </div>
        </details>
      </DocSection>
    </div>
  );
}
